import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import axios from 'axios';

import { detectFaces, drawResults } from '../../helpers/faceApi';
import Button from '../Button/Button';
import Webcam from 'react-webcam';
import EmotionBars, { simulateExpressions } from '../EmotionBars/EmotionBars';
import EmotionChart from '../EmotionChart/EmotionChart';
import '../Camera/Camera.css';

// ── Category Keywords (shared) ──────────────────────────────────
const CATEGORIES = ['All', 'Technology', 'Business', 'Sports', 'Politics', 'Health', 'Science', 'Entertainment', 'World'];

const CATEGORY_KEYWORDS = {
  Technology:    ['tech', 'ai', 'software', 'digital', 'app', 'robot', 'cyber', 'data', 'developer', 'computer', 'startup', 'internet', 'தொழில்நுட்பம்'],
  Business:      ['market', 'economy', 'finance', 'stock', 'trade', 'company', 'corporate', 'bank', 'invest', 'வணிகம்'],
  Sports:        ['sport', 'cricket', 'football', 'match', 'tournament', 'player', 'team', 'champion', 'விளையாட்டு'],
  Politics:      ['politic', 'government', 'election', 'minister', 'parliament', 'vote', 'president', 'அரசியல்'],
  Health:        ['health', 'medical', 'doctor', 'hospital', 'disease', 'vaccine', 'treatment', 'உடல்நலம்'],
  Science:       ['science', 'research', 'space', 'nasa', 'climate', 'discovery', 'அறிவியல்'],
  Entertainment: ['movie', 'film', 'music', 'celebrity', 'actor', 'award', 'சினிமா', 'திரைப்படம்'],
  World:         ['world', 'global', 'international', 'war', 'conflict', 'உலகம்'],
};

// ── Bookmark Helpers ──────────────────────────────────────────────
const BOOKMARK_KEY = 'emonews-bookmarks';
const getBookmarks = () => {
  try { return JSON.parse(localStorage.getItem(BOOKMARK_KEY) || '[]'); }
  catch { return []; }
};
const toggleBookmark = (article) => {
  const current = getBookmarks();
  const idx = current.findIndex(b => b.url === article.url);
  let updated;
  if (idx >= 0) {
    updated = current.filter((_, i) => i !== idx);
  } else {
    updated = [{ ...article, bookmarkedAt: Date.now() }, ...current];
  }
  localStorage.setItem(BOOKMARK_KEY, JSON.stringify(updated));
  return updated;
};

const matchesCategory = (article, category) => {
  if (category === 'All') return true;
  const keywords = CATEGORY_KEYWORDS[category] || [];
  const text = `${article.title || ''} ${article.description || ''}`.toLowerCase();
  return keywords.some(kw => text.includes(kw));
};

// ── Skeleton Card ─────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="news-card skeleton-card" aria-hidden="true">
    <div className="skeleton skeleton-img" />
    <div className="news-card-body">
      <div className="skeleton skeleton-title" />
      <div className="skeleton skeleton-para" />
      <div className="skeleton skeleton-para skeleton-para--short" />
      <div className="skeleton-bars">
        {[...Array(3)].map((_, i) => <div key={i} className="skeleton skeleton-bar" />)}
      </div>
    </div>
  </div>
);

// ── Tamil News Card ───────────────────────────────────────────────
const TamilNewsCard = ({ article, index, detectedEmotion, bookmarks, onToggleBookmark }) => {
  const expressions = useMemo(
    () => simulateExpressions(detectedEmotion, index + 1),
    [detectedEmotion, index]
  );
  const isBookmarked = bookmarks.some(b => b.url === article.url);

  return (
    <div
      className="news-card animate-fade-in"
      style={{ animationDelay: `${index * 0.04}s` }}
      role="article"
      aria-label={article.title}
    >
      <button
        className={classnames('bookmark-btn', isBookmarked && 'bookmark-btn--active')}
        onClick={() => onToggleBookmark(article)}
        aria-label={isBookmarked ? 'புக்மார்க் நீக்கு' : 'புக்மார்க் சேர்'}
        title={isBookmarked ? 'புக்மார்க் நீக்கு' : 'கட்டுரையை சேமி'}
      >
        {isBookmarked ? '★' : '☆'}
      </button>

      {article.urlToImage && (
        <div className="news-card-img-wrapper img-hover-zoom">
          <img src={article.urlToImage} alt={article.title || 'செய்தி'} loading="lazy" />
        </div>
      )}

      <div className="news-card-body">
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          className="news-card-title"
        >
          {article.title}
        </a>

        {article.description && (
          <p className="news-card-desc">{article.description}</p>
        )}

        <div className="news-card-footer">
          {article.source?.name && (
            <span className="news-card-source">{article.source.name}</span>
          )}
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="read-more"
          >
            முழு விவரம் →
          </a>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const Cameratamil = ({ photoMode }) => {
  const camera = useRef(null);
  const cameraCanvas = useRef(null);
  const newsFeedRef = useRef(null);

  const [news, setNews] = useState([]);
  const [detectedEmotion, setDetectedEmotion] = useState('');
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [bookmarks, setBookmarks] = useState(() => getBookmarks());

  const isDetectingRef = useRef(false);

  useEffect(() => {
    const handleStorage = () => setBookmarks(getBookmarks());
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const handleToggleBookmark = useCallback((article) => {
    const updated = toggleBookmark(article);
    setBookmarks(updated);
    window.dispatchEvent(new Event('storage'));
  }, []);

  // ── Tamil News Fetching (UNCHANGED logic) ───────────────────────
  const getNewsForEmotion = useCallback(async (emotion) => {
    if (!emotion) return;

    setIsLoading(true);
    setDetectedEmotion(emotion);
    setActiveCategory('All');

    let query = 'தமிழ்நாடு OR இந்தியா OR உலகம் OR செய்திகள் OR இன்றைய செய்திகள்';
    if (emotion === 'sad') {
      query = 'சிரிப்பு OR நகைச்சுவை OR மகிழ்ச்சி OR வெற்றி OR சாதனை OR நல்ல செய்திகள்';
    } else if (emotion === 'angry') {
      query = 'அமைதி OR தியானம் OR இயற்கை OR ஆன்மீகம் OR ரிலாக்ஸ் OR சுற்றுலா OR தத்துவங்கள்';
    } else if (emotion === 'happy' || emotion === 'neutral') {
      query = 'செய்திகள் OR அரசியல் OR அறிவியல் OR விளையாட்டு OR சினிமா OR தொழில்நுட்பம் OR கல்வி';
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      let response = await axios.get(`${API_URL}/news`, {
        params: { q: query, language: 'ta', pageSize: 100 }
      });

      let articles = response.data.articles || [];

      // Fallback with simpler query
      if (articles.length === 0) {
        const fallbackResponse = await axios.get(`${API_URL}/news`, {
          params: { q: 'தமிழ்நாடு OR செய்திகள்', language: 'ta', pageSize: 50 }
        });
        articles = fallbackResponse.data.articles || [];
      }

      articles = articles.sort(() => Math.random() - 0.5);
      setNews(articles.slice(0, 50));

      setTimeout(() => {
        if (newsFeedRef.current) {
          const yOffset = -100;
          const y = newsFeedRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      console.error('News Fetch Error:', error);

      if (error.response?.status === 426 || !error.response) {
        setNews([
          {
            title: `[சிமுலேஷன்] ${emotion} நிலைகளுக்கான சிறந்த செய்திகள்`,
            description: `தயாரிப்பு சூழலில் வெளி செய்தி ஊட்டம் கட்டுப்படுத்தப்பட்டுள்ளது. உங்கள் ${emotion} மனநிலைக்கான சிமுலேட்டட் செய்திகள் இங்கே காட்டப்படுகின்றன.`,
            url: '#',
            urlToImage: 'https://images.unsplash.com/photo-1620712943543-bcc4628c6bb8?auto=format&fit=crop&q=80&w=800',
            source: { name: 'EmoNews AI' },
          },
          {
            title: 'மனநிலை சீரமைப்பு முறைகள்',
            description: 'மனநிலைக்கு ஏற்ற செய்திகளைப் பார்ப்பது உங்கள் உற்பத்தித் திறனை 40% வரை அதிகரிக்கும்.',
            url: '#',
            urlToImage: 'https://images.unsplash.com/photo-1507413245164-6160d8298b31?auto=format&fit=crop&q=80&w=800',
            source: { name: 'EmoNews AI' },
          },
        ]);
      } else {
        setNews([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── Face Detection (UNCHANGED logic) ────────────────────────────
  const getFaces = useCallback(async () => {
    if (isDetectingRef.current || !isCameraOn || !camera.current || !camera.current.video || camera.current.video.readyState !== 4) return;

    isDetectingRef.current = true;
    try {
      const faces = await detectFaces(camera.current.video);
      if (cameraCanvas.current && faces) {
        await drawResults(camera.current.video, cameraCanvas.current, faces);
      }
      if (faces && faces.length > 0) {
        const expressions = faces[0].expressions;
        const dominantEmotion = Object.keys(expressions).reduce((a, b) =>
          expressions[a] > expressions[b] ? a : b
        );
        if (expressions[dominantEmotion] > 0.65) {
          getNewsForEmotion(dominantEmotion);
          setIsCameraOn(false);
        }
      }
    } catch (error) {
      console.error('Detection Error:', error);
    } finally {
      isDetectingRef.current = false;
    }
  }, [isCameraOn, getNewsForEmotion]);

  useEffect(() => {
    let interval;
    if (!photoMode && isCameraOn) {
      interval = setInterval(getFaces, 1000);
    }
    return () => clearInterval(interval);
  }, [photoMode, isCameraOn, getFaces]);

  const filteredNews = useMemo(() =>
    news.filter(a => matchesCategory(a, activeCategory)),
    [news, activeCategory]
  );

  const MANUAL_EMOTIONS = [
    { id: 'happy',   label: '😊 மகிழ்ச்சி' },
    { id: 'sad',     label: '😢 சோகம்'     },
    { id: 'angry',   label: '😡 கோபம்'     },
    { id: 'neutral', label: '😐 அமைதி'     },
  ];

  const tamilLabel = (emotion) => {
    const map = { happy: 'மகிழ்ச்சி', sad: 'சோகம்', angry: 'கோபம்', neutral: 'அமைதி' };
    return map[emotion] || emotion;
  };

  return (
    <div className="camera">
      <div className="container">
        <div className="section-header animate-fade-in">
          <h2>உங்கள் மனநிலைக்கேற்ற செய்திகள்</h2>
          {detectedEmotion && (
            <span className="section-header__badge">
              {tamilLabel(detectedEmotion)} நிலை
            </span>
          )}
        </div>

        <div className="main-layout">
          <aside className="camera-preview">
            {/* Scanner Card */}
            <div className="camera-block glass-card">
              <div className="block-label">உணர்ச்சி ஆய்வு</div>
              <div className="camera__wrapper small-cam">
                {isCameraOn ? (
                  <>
                    <Webcam audio={false} ref={camera} width="100%" height="auto" mirrored={true}
                      aria-label="முக உணர்ச்சி கண்டறிவு கேமரா" />
                    <canvas
                      className={classnames('webcam-overlay', photoMode && 'webcam-overlay--hidden')}
                      ref={cameraCanvas}
                      aria-hidden="true"
                    />
                  </>
                ) : (
                  <div className="camera__off">
                    <p>{detectedEmotion ? 'ஆய்வு முடிந்தது' : 'சிஸ்டம் தயார்'}</p>
                    {detectedEmotion && (
                      <div className="last-emotion-tag">{tamilLabel(detectedEmotion).toUpperCase()}</div>
                    )}
                  </div>
                )}
                {isCameraOn && <div className="scanner-bezel" aria-hidden="true" />}
              </div>
              <div className="camera__controls">
                <Button
                  onClick={() => setIsCameraOn(!isCameraOn)}
                  className={classnames('toggle-btn btn-small', !isCameraOn && 'button--primary')}
                  aria-label={isCameraOn ? 'ஆய்வை நிறுத்து' : 'ஸ்கேன் தொடங்கு'}
                >
                  {isCameraOn ? '🛑 ஆய்வை நிறுத்து' : '⚡ ஸ்கேன் தொடங்கு'}
                </Button>
              </div>
            </div>

            {/* Manual Selector */}
            <div className="manual-selector glass-card">
              <div className="block-label">நேரடித் தேர்வு</div>
              <div className="selector-content">
                <div className="emotion-buttons">
                  {MANUAL_EMOTIONS.map(emo => (
                    <button
                      key={emo.id}
                      className={classnames('emo-btn-large', detectedEmotion === emo.id && `active-${emo.id}`)}
                      onClick={() => getNewsForEmotion(emo.id)}
                      disabled={isLoading}
                      aria-label={`${emo.label} செய்திகள் காண்`}
                      aria-pressed={detectedEmotion === emo.id}
                    >
                      <span className="emo-icon" role="img" aria-hidden>{emo.label.split(' ')[0]}</span>
                      <span className="emo-text">{emo.label.split(' ')[1]}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Analytics Chart */}
            {news.length > 0 && (
              <EmotionChart
                articles={filteredNews}
                detectedEmotion={detectedEmotion}
                title="உணர்ச்சி பகுப்பாய்வு"
              />
            )}
          </aside>

          <main className="news-feed" ref={newsFeedRef}>
            {news.length > 0 && !isLoading && (
              <div className="category-filter" role="group" aria-label="வகை வடிகட்டி">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    className={classnames('filter-chip', activeCategory === cat && 'active')}
                    onClick={() => setActiveCategory(cat)}
                    aria-pressed={activeCategory === cat}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {news.length > 0 && !isLoading && (
              <p className="news-result-count">
                {filteredNews.length} கட்டுரை{activeCategory !== 'All' ? ` (${activeCategory})` : ''}
              </p>
            )}

            <div className="news-grid">
              {isLoading ? (
                [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
              ) : filteredNews.length > 0 ? (
                filteredNews.map((article, index) => (
                  <TamilNewsCard
                    key={`${article.url}-${index}`}
                    article={article}
                    index={index}
                    detectedEmotion={detectedEmotion}
                    bookmarks={bookmarks}
                    onToggleBookmark={handleToggleBookmark}
                  />
                ))
              ) : news.length > 0 ? (
                <div className="no-results glass-card animate-fade-in">
                  <span role="img" aria-label="no results" style={{ fontSize: '2.5rem' }}>🔍</span>
                  <h3>{activeCategory} வகையில் கட்டுரைகள் இல்லை</h3>
                  <p>வேறு வகையை தேர்வு செய்யவும்.</p>
                  <button className="filter-chip" onClick={() => setActiveCategory('All')}>
                    அனைத்தும் காட்டு
                  </button>
                </div>
              ) : detectedEmotion ? (
                <div className="no-results glass-card animate-fade-in">
                  <span role="img" aria-label="satellite" style={{ fontSize: '2.5rem' }}>📡</span>
                  <h3>மன்னிக்கவும்!</h3>
                  <p>தற்போது உங்கள் மனநிலைக்கான நேரடிச் செய்திகள் கிடைக்கவில்லை.</p>
                </div>
              ) : null}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Cameratamil;
